/**
  * Authentication context for Instagram Commentor
  *
  * Provides Firebase Authentication state and methods throughout the app.
  */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    User as FirebaseUser,
    signInWithPopup,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    getIdToken,
} from 'firebase/auth';
import {
    initializeFirebase,
    getFirebaseAuth,
    getGoogleProvider,
    getAppleProvider,
    isFirebaseInitialized,
} from '../config/firebase';
import { notificationService } from '../services/notificationService';
import { subscribeToUserCredits } from '../services/firestoreListeners';

// User type with credit balance
export interface User {
    uid: string;
    email: string;
    displayName: string;
    photoUrl: string | null;
    creditBalance: number;
}

// Auth context type
interface AuthContextType {
    user: User | null;
    firebaseUser: FirebaseUser | null;
    loading: boolean;
    error: string | null;
    signInWithGoogle: () => Promise<void>;
    signInWithApple: () => Promise<void>;
    signOut: () => Promise<void>;
    deleteAccount: () => Promise<void>;
    getIdToken: () => Promise<string | null>;
    refreshUser: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Provider component
interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Verify token with backend and get user info
    const verifyTokenWithBackend = async (idToken: string, fbUser: FirebaseUser): Promise<User | null> => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id_token: idToken }),
            });

            if (!response.ok) {
                throw new Error('Token verification failed');
            }

            const data = await response.json();
            if (data.authenticated && data.user) {
                return {
                    uid: data.user.firebaseUid,
                    email: data.user.email,
                    displayName: data.user.displayName,
                    photoUrl: data.user.photoUrl,
                    creditBalance: data.user.creditBalance ?? 0,
                };
            }
            return null;
        } catch (err) {
            console.error('Error verifying token with backend:', err);
            return null;
        }
    };

    // Refresh user data from backend
    const refreshUser = async (): Promise<void> => {
        if (!firebaseUser) return;

        try {
            const idToken = await firebaseUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/auth/me`, {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setUser({
                    uid: data.firebaseUid,
                    email: data.email,
                    displayName: data.displayName,
                    photoUrl: data.photoUrl,
                    creditBalance: data.creditBalance ?? 0,
                });
            }
        } catch (err) {
            console.error('Error refreshing user:', err);
        }
    };

    // Initialize Firebase and listen for auth state changes
    useEffect(() => {
        let unsubscribe: (() => void) | null = null;
        let unsubscribeCredits: (() => void) | null = null;

        const init = async () => {
            try {
                // Initialize Firebase first
                await initializeFirebase();
                const auth = getFirebaseAuth();

                // Now listen for auth state changes
                unsubscribe = onAuthStateChanged(auth, async (fbUser: FirebaseUser | null) => {
                    setLoading(true);
                    setError(null);

                    // Clean up previous credits listener
                    if (unsubscribeCredits) {
                        unsubscribeCredits();
                        unsubscribeCredits = null;
                    }

                    if (fbUser && !fbUser.isAnonymous) {
                        setFirebaseUser(fbUser);
                        try {
                            const idToken = await fbUser.getIdToken();
                            const userData = await verifyTokenWithBackend(idToken, fbUser);
                            setUser(userData);

                            // Start real-time credit balance listener
                            if (userData?.uid) {
                                unsubscribeCredits = subscribeToUserCredits(
                                    userData.uid,
                                    (creditBalance: number) => {
                                        setUser(prev => {
                                            if (!prev) return prev;
                                            return {
                                                ...prev,
                                                creditBalance,
                                            };
                                        });
                                    },
                                    (error: Error) => {
                                        console.error('Credit balance listener error:', error);
                                    }
                                );
                            }
                        } catch (err) {
                            console.error('Error getting user data:', err);
                            setUser(null);
                        }
                    } else {
                        // No user or anonymous user - require login
                        setFirebaseUser(null);
                        setUser(null);
                    }

                    setLoading(false);
                });
            } catch (err: any) {
                console.error('Failed to initialize Firebase:', err);
                setLoading(false);
                setError(err?.message || 'Failed to initialize authentication');
            }
        };

        init();

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
            if (unsubscribeCredits) {
                unsubscribeCredits();
            }
        };
    }, []);

    // Sign in with Google
    const signInWithGoogle = async (): Promise<void> => {
        setError(null);
        try {
            if (!isFirebaseInitialized()) {
                await initializeFirebase();
            }
            const auth = getFirebaseAuth();
            const googleProvider = getGoogleProvider();
            const result = await signInWithPopup(auth, googleProvider);
            const idToken = await result.user.getIdToken();
            const userData = await verifyTokenWithBackend(idToken, result.user);
            setUser(userData);

            // Request push notification permission
            notificationService.requestPermission().then((granted) => {
                console.log('Push notification permission:', granted ? 'granted' : 'denied');
            });
        } catch (err: any) {
            console.error('Google sign-in error:', err);
            if (err.code === 'auth/popup-closed-by-user') {
                setError('Sign-in cancelled');
            } else if (err.code === 'auth/popup-blocked') {
                setError('Pop-up blocked. Please allow pop-ups for this site.');
            } else {
                setError(err.message || 'Sign-in failed');
            }
            throw err;
        }
    };

    // Sign in with Apple
    const signInWithApple = async (): Promise<void> => {
        setError(null);
        try {
            if (!isFirebaseInitialized()) {
                await initializeFirebase();
            }
            const auth = getFirebaseAuth();
            const appleProvider = getAppleProvider();
            const result = await signInWithPopup(auth, appleProvider);
            const idToken = await result.user.getIdToken();
            const userData = await verifyTokenWithBackend(idToken, result.user);
            setUser(userData);

            // Request push notification permission
            notificationService.requestPermission().then((granted) => {
                console.log('Push notification permission:', granted ? 'granted' : 'denied');
            });
        } catch (err: any) {
            console.error('Apple sign-in error:', err);
            if (err.code === 'auth/popup-closed-by-user') {
                setError('Sign-in cancelled');
            } else if (err.code === 'auth/popup-blocked') {
                setError('Pop-up blocked. Please allow pop-ups for this site.');
            } else {
                setError(err.message || 'Sign-in failed');
            }
            throw err;
        }
    };

    // Sign out
    const signOut = async (): Promise<void> => {
        try {
            if (!isFirebaseInitialized()) {
                return; // Nothing to sign out from
            }

            // Unregister FCM token before signing out
            await notificationService.unregisterToken();

            const auth = getFirebaseAuth();
            await firebaseSignOut(auth);
            setUser(null);
            setFirebaseUser(null);
        } catch (err: any) {
            console.error('Sign-out error:', err);
            setError(err.message || 'Sign-out failed');
            throw err;
        }
    };

    // Delete account permanently
    const deleteAccount = async (): Promise<void> => {
        if (!firebaseUser) {
            throw new Error('Not signed in');
        }

        try {
            // Call backend to delete all data (including Firebase Auth user)
            const idToken = await firebaseUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/auth/account`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to delete account');
            }

            // Unregister FCM token
            await notificationService.unregisterToken();

            // Clear local state (Firebase user is already deleted by backend)
            setUser(null);
            setFirebaseUser(null);
        } catch (err: any) {
            console.error('Delete account error:', err);
            setError(err.message || 'Failed to delete account');
            throw err;
        }
    };

    // Get ID token for API calls
    const getIdTokenFunc = async (): Promise<string | null> => {
        if (!firebaseUser) return null;
        try {
            return await getIdToken(firebaseUser);
        } catch (err) {
            console.error('Error getting ID token:', err);
            return null;
        }
    };

    const value: AuthContextType = {
        user,
        firebaseUser,
        loading,
        error,
        signInWithGoogle,
        signInWithApple,
        signOut,
        deleteAccount,
        getIdToken: getIdTokenFunc,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
